import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone, FileRejection } from 'react-dropzone';
import toast from 'react-hot-toast';
import { CheckIcon, XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { signalRService } from '../services/signalrService';
import ProgressIndicator from './ProgressIndicator';
import { api } from '../utils/api';
import { shopifyService, ShopifyProduct } from '../services/shopifyService';
import { CategoryCombobox } from './CategoryCombobox';
import AutoGrowTextarea from './AutoGrowTextarea';
import KeywordSelector from './KeywordSelector';

// Temporary inline types to resolve import issues
interface ProductMetadata {
  name: string;
  description: string;
  price: number;
  currency: string;
  category: string;
  materials: string[];
  dimensions: {
    height: number;
    width: number;
    depth: number;
    weight: number;
  };
  tags: string[];
}

interface MultiLanguageMetadata {
  de: ProductMetadata;
  en: ProductMetadata;
  fr: ProductMetadata;
  es: ProductMetadata;
}

interface RefinedProductData {
  refinedMetadata: MultiLanguageMetadata;
  sharedInformation: Record<string, unknown>;
  originalData: Record<string, unknown>;
  correlationId: string;
  agentSummary: string;
  shopify_product_id?: string;
  images?: string[];
}

interface UploadProgress {
  stage: string;
  progress: number;
  message: string;
}

type FormState = 'initial' | 'analyzed' | 'processing';

// Field change tracking types for issue #25
interface FieldChange {
  original: unknown;
  current: unknown;
  isChanged: boolean;
}

interface FieldChanges {
  [field: string]: FieldChange;
}

interface LanguageFieldChanges {
  [language: string]: FieldChanges;
}

type ChangeType = 'title' | 'description' | 'category' | 'technical' | 'tags' | 'other';

interface Language {
  code: string;
  name: string;
  flag: string;
}

const LANGUAGES: Language[] = [
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' }
];

export interface UploadFormEnhancedProps {
  refinedData?: RefinedProductData | null;
  onClearRefinedData?: () => void;
}

const UploadFormEnhanced: React.FC<UploadFormEnhancedProps> = ({ 
  refinedData, 
  onClearRefinedData
}) => {
  const navigate = useNavigate();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [multiLanguageMetadata, setMultiLanguageMetadata] = useState<MultiLanguageMetadata | null>(null);
  const [originalMetadata, setOriginalMetadata] = useState<MultiLanguageMetadata | null>(null);  // Store original for change detection
  const [fieldChanges, setFieldChanges] = useState<LanguageFieldChanges>({});  // Track field-level changes
  const [formState, setFormState] = useState<FormState>('initial');
  const [uploading, setUploading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleCancel = () => {
    handleCancelAnalysis();
  };
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const [correlationId, setCorrelationId] = useState<string | null>(null);
  const [connectionId, setConnectionId] = useState<string>('');
  const [imageUrls, setImageUrls] = useState<string[]>([]);  // Store image URLs from analysis
  const [activeLanguage, setActiveLanguage] = useState<string>('de');
  const [reviewedLanguages, setReviewedLanguages] = useState<Set<string>>(new Set());
  const [hasChanges, setHasChanges] = useState<Record<string, boolean>>({});
  const [nameConflict, setNameConflict] = useState<{ exists: boolean; existingProduct?: { id: string; title: string; handle: string } } | null>(null);
  const [validatingName, setValidatingName] = useState(false);
  const [draftProductId, setDraftProductId] = useState<string | null>(null);  // Track draft product ID
  const [existingKeywords, setExistingKeywords] = useState<string[]>([]);  // Keywords from existing products

  // Optional artist hints for AI analysis
  const [nameIdea, setNameIdea] = useState<string>('');
  const [inspiration, setInspiration] = useState<string>('');
  const [heightCm, setHeightCm] = useState<string>('');
  const [widthCm, setWidthCm] = useState<string>('');
  const [depthCm, setDepthCm] = useState<string>('');
  const [weightGrams, setWeightGrams] = useState<string>('');
  const [priceIdea, setPriceIdea] = useState<string>('');

  // Helper function to track field changes with original values
  const trackFieldChange = useCallback((language: string, field: string, newValue: unknown, originalValue: unknown) => {
    const isChanged = JSON.stringify(newValue) !== JSON.stringify(originalValue);
    setFieldChanges(prev => ({
      ...prev,
      [language]: {
        ...prev[language],
        [field]: { original: originalValue, current: newValue, isChanged }
      }
    }));
  }, []);

  // Helper to get changed fields for a language
  const getChangedFields = useCallback((language: string): string[] => {
    const changes = fieldChanges[language];
    if (!changes) return [];
    return Object.entries(changes)
      .filter(([, change]) => change.isChanged)
      .map(([field]) => field);
  }, [fieldChanges]);

  // Helper to categorize changes by type
  const categorizeChanges = useCallback((changedFields: string[]): ChangeType[] => {
    const types: Set<ChangeType> = new Set();
    for (const field of changedFields) {
      if (field === 'name') types.add('title');
      else if (field === 'description' || field === 'short_description') types.add('description');
      else if (field === 'category' || field === 'product_type') types.add('category');
      else if (field === 'tags') types.add('tags');
      else if (['price', 'dimensions', 'weight', 'sku', 'materials'].includes(field)) types.add('technical');
      else types.add('other');
    }
    return Array.from(types);
  }, []);

  // Helper to check if a specific field is changed
  const isFieldChanged = useCallback((language: string, field: string): boolean => {
    return fieldChanges[language]?.[field]?.isChanged ?? false;
  }, [fieldChanges]);

  // Helper to get CSS class for field highlighting when changed
  const getFieldClassName = useCallback((language: string, field: string, baseClass: string): string => {
    const changed = isFieldChanged(language, field);
    if (changed) {
      return `${baseClass} ring-2 ring-amber-400 ring-offset-1 bg-amber-50`;
    }
    return baseClass;
  }, [isFieldChanged]);

  // Initialize SignalR connection and register progress callback
  useEffect(() => {
    const initializeSignalR = async () => {
      try {
        const connId = await signalRService.connect();
        setConnectionId(connId);
        // console.log('SignalR connected with ID:', connId);
        
        // Register callback to receive progress updates from backend
        signalRService.registerProgressCallback('upload-form', (update) => {
          // console.log('SignalR progress update received:', update);
          setUploadProgress({
            stage: update.stage,
            progress: update.progress,
            message: update.message
          });
        });
      } catch (error) {
        console.error('Failed to connect to SignalR:', error);
        // Fallback to local connection ID
        setConnectionId(`conn-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
      }
    };

    initializeSignalR();

    // Cleanup on unmount
    return () => {
      signalRService.unregisterProgressCallback('upload-form');
      signalRService.disconnect();
    };
  }, []);

  // Fetch existing keywords from products for suggestions
  useEffect(() => {
    const fetchExistingKeywords = async () => {
      try {
        const response = await shopifyService.getProducts(100);
        if (response.success && response.products) {
          const allTags = new Set<string>();
          response.products.forEach((product: ShopifyProduct) => {
            const tags = product.tags;
            if (tags) {
              const tagList = typeof tags === 'string' 
                ? tags.split(',').map(t => t.trim().toLowerCase()) 
                : tags.map(t => t.toLowerCase());
              tagList.filter(Boolean).forEach(tag => allTags.add(tag));
            }
          });
          setExistingKeywords(Array.from(allTags).sort());
        }
      } catch (error) {
        console.error('Failed to fetch existing keywords:', error);
      }
    };

    fetchExistingKeywords();
  }, []);

  // Handle refined data from ProductList
  useEffect(() => {
    if (refinedData) {      setMultiLanguageMetadata(refinedData.refinedMetadata);
      setCorrelationId(refinedData.correlationId);
      setFormState('analyzed');
      
      // If images are provided from refined data, convert them to File objects for display
      if (refinedData.images && refinedData.images.length > 0) {
        const convertBase64ToFile = async (base64: string, index: number): Promise<File> => {
          const response = await fetch(base64);
          const blob = await response.blob();
          return new File([blob], `refined-image-${index}.jpg`, { type: 'image/jpeg' });
        };
          Promise.all(
          refinedData.images.map((base64: string, index: number) => convertBase64ToFile(base64, index))
        ).then(files => {
          setSelectedFiles(files);
        }).catch(error => {
          console.error('Failed to convert refined images:', error);
        });
      }
      
      toast.success('Verfeinertes Produkt geladen! Sie können die Daten überprüfen und anpassen.');
    }
  }, [refinedData]);

  // WebSocket connection for real-time updates (mock implementation)
  useEffect(() => {
    if (connectionId && formState === 'processing') {
      // In a real implementation, this would connect to SignalR
      // console.log(`WebSocket connection established: ${connectionId}`);
    }
  }, [connectionId, formState]);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
      'image/heic': ['.heic'],
      'image/heif': ['.heif']
    },
    multiple: true,
    maxFiles: 5,
    // Important for iOS: don't prevent default on click, use noKeyboard for better touch handling
    useFsAccessApi: false, // Disable File System Access API for better iOS compatibility
    maxSize: 10 * 1024 * 1024, // 10MB per file
    onDrop: useCallback((acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      // Handle rejected files
      if (rejectedFiles.length > 0) {
        rejectedFiles.forEach(({ file, errors }) => {
          errors.forEach((error: { code: string; message: string }) => {
            if (error.code === 'file-too-large') {
              toast.error(`${file.name} ist zu groß (max. 10MB)`);
            } else if (error.code === 'file-invalid-type') {
              toast.error(`${file.name} ist kein unterstütztes Bildformat`);
            } else {
              toast.error(`${file.name}: ${error.message}`);
            }
          });
        });
      }

      // Handle accepted files
      if (acceptedFiles.length > 0) {
        const totalFiles = selectedFiles.length + acceptedFiles.length;
        if (totalFiles > 5) {
          toast.error('Maximal 5 Bilder erlaubt');
          return;
        }
        setSelectedFiles(prev => [...prev, ...acceptedFiles]);
        toast.success(`${acceptedFiles.length} Bild(er) hinzugefügt`);
      }
    }, [selectedFiles]),
    onDropRejected: () => {
      // Rejected files are logged via react-dropzone error callbacks if needed
    },
    onError: (error: Error) => {
      console.error('Dropzone error:', error);
      toast.error('Fehler beim Hochladen der Dateien');
    }
  });

  const handleRootKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      open();
    }
  }, [open]);

  const handleSelectFilesClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    open();
  }, [open]);

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleMetadataChange = (language: string, field: string, value: string | number | boolean | string[] | { height?: number; width?: number; depth?: number; weight?: number }) => {
    if (!multiLanguageMetadata) return;

    // Get the original value for change tracking
    const langKey = language as keyof MultiLanguageMetadata;
    const originalLangData = originalMetadata?.[langKey];
    const originalValue = originalLangData ? (originalLangData as unknown as Record<string, unknown>)[field] : undefined;

    // Track the field change (newValue first, then originalValue)
    trackFieldChange(language, field, value, originalValue);

    const updatedMetadata = {
      ...multiLanguageMetadata,
      [language]: {
        ...multiLanguageMetadata[langKey],
        [field]: value
      }
    };

    setMultiLanguageMetadata(updatedMetadata);
    setHasChanges(prev => ({ ...prev, [language]: true }));
  };

  const handleArrayChange = (language: string, field: 'materials' | 'tags', value: string) => {
    if (!multiLanguageMetadata) return;

    const newArray = value.split(',').map(item => item.trim()).filter(Boolean);
    
    handleMetadataChange(language, field, newArray);
  };

  const markLanguageAsReviewed = (language: string) => {
    setReviewedLanguages(prev => new Set([...Array.from(prev), language]));
    toast.success(`${LANGUAGES.find(l => l.code === language)?.name} überprüft!`);
  };
  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  const handleAnalyze = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedFiles.length === 0) {
      toast.error('Bitte wählen Sie mindestens ein Bild aus');
      return;
    }

    setUploading(true);
    setFormState('processing');
    setUploadProgress({ stage: 'started', progress: 1, message: 'Analyse wird gestartet…' });
    
    // Create new AbortController
    abortControllerRef.current = new AbortController();

    try {
      // Convert files to base64
      setUploadProgress({ stage: 'converting', progress: 15, message: 'Bilder werden konvertiert…' });
      const base64Images = await Promise.all(
        selectedFiles.map(file => convertFileToBase64(file))
      );

      if (abortControllerRef.current?.signal.aborted) return;

      const totalSteps = selectedFiles.length + 1;
      let completedSteps = 0;
      const updateAnalyzingProgress = (stepLabel: string) => {
        completedSteps += 1;
        const percent = Math.min(15 + Math.round((completedSteps / totalSteps) * 75), 90);
        setUploadProgress({
          stage: 'analyzing',
          progress: percent,
          message: `KI-Analyse läuft… (${stepLabel})`
        });
      };

      selectedFiles.forEach((file, index) => {
        updateAnalyzingProgress(`Bild ${index + 1} von ${selectedFiles.length}`);
      });

      // Build artist hints object with optional inputs
      const artistHints: Record<string, unknown> = {};
      if (nameIdea.trim()) artistHints.nameIdea = nameIdea.trim();
      if (inspiration.trim()) artistHints.inspiration = inspiration.trim();
      if (priceIdea.trim()) artistHints.priceIdea = parseFloat(priceIdea) || undefined;
      
      // Add dimensions if any provided
      const dimensions: Record<string, number> = {};
      if (heightCm.trim()) dimensions.height = parseFloat(heightCm) || 0;
      if (widthCm.trim()) dimensions.width = parseFloat(widthCm) || 0;
      if (depthCm.trim()) dimensions.depth = parseFloat(depthCm) || 0;
      if (weightGrams.trim()) dimensions.weight = parseFloat(weightGrams) || 0;
      if (Object.keys(dimensions).length > 0) artistHints.dimensions = dimensions;

      const analyzeResponse = await api.post('/analyze', {
        images: base64Images,
        artistHints: Object.keys(artistHints).length > 0 ? artistHints : undefined
      }, {
        showToast: true,
        connectionId,
        signal: abortControllerRef.current.signal
      });

      if (analyzeResponse.data) {
        const result = analyzeResponse.data as {
          analyzedMetadata: MultiLanguageMetadata;
          correlationId: string;
          imageUrls?: string[];
        };
        
        // Keep form in processing state - we'll navigate directly to product page
        // Don't show the analyzed form UI at all
        setUploadProgress({ stage: 'saving', progress: 95, message: 'Entwurf wird gespeichert...' });
        
        // Show toast with analysis success
        toast.success(
          `Analyse abgeschlossen! Entwurf wird gespeichert...\n\n` +
          `Referenz-ID: ${result.correlationId.substring(0, 8)}`,
          { duration: 3000 }
        );
        
        // Save draft immediately and navigate directly to product page
        // (clemensv's requirement: skip the upload page, go straight to product page)
        const draftProductId = await saveDraftAutomatically(result.analyzedMetadata, result.imageUrls || [], result.correlationId);
        
        if (draftProductId) {
          // Update progress to show navigation is happening
          setUploadProgress({ stage: 'navigating', progress: 100, message: 'Navigiere zur Produktseite...' });
          toast.success('Entwurf gespeichert! Öffne Produktseite...', { duration: 2000 });
          
          // Navigate directly to product page with minimal delay
          setTimeout(() => {
            navigate(`/products/${draftProductId}`);
          }, 300);
        } else {
          // Draft save failed - fall back to showing analyzed form so user can try manual save
          console.warn('Auto-save failed, showing analyzed form for manual intervention');
          setMultiLanguageMetadata(result.analyzedMetadata);
          setOriginalMetadata(JSON.parse(JSON.stringify(result.analyzedMetadata)));
          setFieldChanges({});
          setCorrelationId(result.correlationId);
          if (result.imageUrls && result.imageUrls.length > 0) {
            setImageUrls(result.imageUrls);
          }
          setFormState('analyzed');
          setActiveLanguage('de');
          setUploading(false);
          setUploadProgress(null);
          toast.error('Automatisches Speichern fehlgeschlagen. Bitte manuell als Entwurf speichern.', { duration: 5000 });
        }
      } else {
        const errorMessage = analyzeResponse.error || 'Unbekannter Fehler';
        toast.error(
          `Analyse fehlgeschlagen: ${errorMessage}\n\n` +
          (correlationId ? `Referenz-ID für Support: ${correlationId.substring(0, 8)}` : 'Bitte versuchen Sie es erneut.'),
          { duration: 8000 }
        );
        setUploading(false);
        setUploadProgress(null);
        setFormState('initial');
      }
    } catch (error: unknown) {
      if (error instanceof DOMException && (error.name === 'AbortError' || error.message === 'The user aborted a request.')) {
        console.log('Analysis aborted by user');
        return;
      }
      if (error instanceof Error) {
        console.error('Analysis failed:', error);
      } else {
        console.error('Analysis failed:', String(error));
      }
      const errorCorrelationId = correlationId || 'N/A';
      toast.error(
        `Analyse fehlgeschlagen. Bitte versuchen Sie es erneut.\n\n` +
        `Referenz-ID für Support: ${errorCorrelationId.substring(0, 8)}`,
        { duration: 8000 }
      );
      setUploading(false);
      setUploadProgress({ stage: 'failed', progress: 0, message: 'Analyse fehlgeschlagen' });
      setFormState('initial');
    } finally {
      abortControllerRef.current = null;
    }
  };

  const handleSaveDraft = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (selectedFiles.length === 0) {
      toast.error('Bitte wählen Sie mindestens ein Bild aus');
      return;
    }

    // Generate title from nameIdea or use a timestamp-based default
    const title = nameIdea.trim() || `Entwurf ${new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`;

    setUploading(true);
    setUploadProgress({ stage: 'started', progress: 10, message: 'Entwurf wird erstellt...' });

    try {
      // Convert files to base64
      setUploadProgress({ stage: 'converting', progress: 30, message: 'Bilder werden konvertiert…' });
      const base64Images = await Promise.all(
        selectedFiles.map(file => convertFileToBase64(file))
      );

      setUploadProgress({ stage: 'saving', progress: 60, message: 'Entwurf wird gespeichert...' });

      const draftResponse = await api.post('/upload-draft', {
        images: base64Images,
        title: title,
        description: inspiration.trim() || ''  // Use inspiration as description if provided
      }, {
        showToast: true,
        connectionId
      });

      if (draftResponse.data) {
        const result = draftResponse.data as { productId?: string; product_id?: string; shopify_product_id?: string };
        const productId = result.productId || result.product_id || result.shopify_product_id;

        setUploadProgress({ stage: 'complete', progress: 100, message: 'Entwurf erfolgreich gespeichert!' });
        toast.success('Entwurf erfolgreich gespeichert! Öffne Produktseite...', { duration: 2000 });

        // Navigate to product page
        if (productId) {
          setTimeout(() => {
            navigate(`/products/${productId}`);
          }, 300);
        } else {
          // Fallback to products list
          setTimeout(() => {
            navigate('/products');
          }, 300);
        }
      } else {
        const errorMessage = draftResponse.error || 'Unbekannter Fehler';
        toast.error(`Entwurf speichern fehlgeschlagen: ${errorMessage}`);
        setUploading(false);
        setUploadProgress(null);
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.error('Draft save failed:', error);
      } else {
        console.error('Draft save failed:', String(error));
      }
      toast.error('Entwurf speichern fehlgeschlagen. Bitte versuchen Sie es erneut.');
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const handleCancelAnalysis = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setUploading(false);
    setFormState('initial');
    setUploadProgress(null);
    toast('Analyse abgebrochen', { icon: '🛑' });
  };

  const handleRefine = async (language: string) => {
    if (!multiLanguageMetadata || !hasChanges[language]) {
      toast('Keine Änderungen zu verfeinern', { icon: 'ℹ️' });
      return;
    }

    setUploading(true);
    try {
      const changes = multiLanguageMetadata[language as keyof MultiLanguageMetadata];
      
      // Build refinement context with field-level change information
      const changedFields = getChangedFields(language);
      const changeTypes = categorizeChanges(changedFields);
      
      // Create detailed field changes for the backend
      const fieldChangeDetails: Record<string, { original: unknown; current: unknown }> = {};
      for (const field of changedFields) {
        const change = fieldChanges[language]?.[field];
        if (change) {
          fieldChangeDetails[field] = {
            original: change.original,
            current: change.current
          };
        }
      }
      
      const refineResponse = await api.post('/refine', {
        changes,
        language,
        correlationId,
        // Smart refinement context
        refinementContext: {
          changedFields,
          changeTypes,
          fieldChangeDetails,
          allLanguagesData: multiLanguageMetadata,
          originalData: originalMetadata
        }
      }, {
        showToast: true,
        connectionId
      });      if (refineResponse.data) {
        const result = refineResponse.data as {
          refinedMetadata: Record<string, unknown>;
          // Backend may return cross-language updates
          crossLanguageUpdates?: Record<string, Record<string, unknown>>;
        };
        
        // Apply smart refinements to current language
        let updatedMetadata = {
          ...multiLanguageMetadata,
          [language]: {
            ...multiLanguageMetadata[language as keyof MultiLanguageMetadata],
            ...result.refinedMetadata
          }
        };
        
        // Apply cross-language updates if backend returns them
        if (result.crossLanguageUpdates) {
          for (const [lang, updates] of Object.entries(result.crossLanguageUpdates)) {
            if (lang !== language) {
              updatedMetadata = {
                ...updatedMetadata,
                [lang]: {
                  ...updatedMetadata[lang as keyof MultiLanguageMetadata],
                  ...updates
                }
              };
            }
          }
        }
        
        setMultiLanguageMetadata(updatedMetadata);
        // Update original metadata to reflect the refined state
        setOriginalMetadata(JSON.parse(JSON.stringify(updatedMetadata)));
        // Clear field changes for the language since we've applied refinements
        setFieldChanges(prev => ({ ...prev, [language]: {} }));
        setHasChanges(prev => ({ ...prev, [language]: false }));
        setUploading(false);
        toast.success(`Verfeinerung für ${LANGUAGES.find(l => l.code === language)?.name} abgeschlossen!`);
      } else {
        const errorMessage = refineResponse.error || 'Unbekannter Fehler';
        toast.error(`Verfeinerung fehlgeschlagen: ${errorMessage}`);
        setUploading(false);
      }
    } catch (error) {
      console.error('Refinement failed:', error);
      toast.error('Verfeinerung fehlgeschlagen. Bitte versuchen Sie es erneut.');
      setUploading(false);
    }
  };

  const saveDraftAutomatically = async (metadata: MultiLanguageMetadata, imageUrls: string[], correlationId: string): Promise<string | null> => {
    try {
      console.log('Auto-saving product as draft to prevent data loss...');
      
      const uploadResponse = await api.post('/upload', {
        metadata,
        reviewedLanguages: [], // Draft doesn't require review
        correlationId,
        imageUrls,
        status: 'draft'
      }, {
        showToast: false // Don't show toast for automatic background save
      });

      if (uploadResponse.data) {
        const responseData = uploadResponse.data as { product_id?: string; shopify_product_id?: string; productId?: string };
        const productId = responseData.product_id || responseData.shopify_product_id || responseData.productId;
        
        if (productId) {
          setDraftProductId(productId);
          console.log(`Draft saved successfully with ID: ${productId}`);
          return productId;
        }
      } else {
        console.warn('Draft save returned no data');
      }
    } catch (error) {
      console.error('Auto-save draft failed:', error);
      // Don't show error toast for background save - just log it
      // User can still manually save as draft if needed
    }
    return null;
  };

  const handlePublish = async (status: 'active' | 'draft' = 'active') => {
    if (status === 'active' && reviewedLanguages.size !== LANGUAGES.length) {
      const unreviewed = LANGUAGES.filter(lang => !reviewedLanguages.has(lang.code));
      toast.error(`Bitte überprüfen Sie alle Sprachen: ${unreviewed.map(l => l.name).join(', ')}`);
      return;
    }

    if (!multiLanguageMetadata) {
      toast.error('Keine Metadaten zum Veröffentlichen vorhanden');
      return;
    }

    // Skip name validation for product updates (when refinedData has shopify_product_id)
    const isUpdate = !!refinedData?.shopify_product_id;

    // Validate product name for new products
    if (!isUpdate && status === 'active') {
      const productName = multiLanguageMetadata.de?.name || multiLanguageMetadata.en?.name;
      if (!productName || productName.trim() === '') {
        toast.error('Produktname ist erforderlich');
        return;
      }

      setValidatingName(true);
      setUploadProgress({ stage: 'validating', progress: 10, message: 'Produktname wird überprüft...' });

      try {
        const validationResult = await shopifyService.validateProductName(productName);
        
        if (validationResult.exists) {
          setNameConflict({
            exists: true,
            existingProduct: validationResult.existing_product
          });
          toast.error(`Ein Produkt mit dem Namen "${validationResult.existing_product?.title}" existiert bereits. Bitte wählen Sie einen anderen Namen.`);
          setValidatingName(false);
          setUploadProgress(null);
          return;
        }
        
        setNameConflict(null);
        setValidatingName(false);
      } catch (error) {
        console.error('Name validation failed:', error);
        // Continue with publishing if validation fails - don't block the user
        setValidatingName(false);
      }
    }

    setUploading(true);
    setUploadProgress({ stage: 'publishing', progress: 25, message: 'Veröffentlichung wird vorbereitet...' });    try {
      setUploadProgress({ stage: 'publishing', progress: 75, message: 'Produkt wird hochgeladen...' });

      const uploadResponse = await api.post('/upload', {
        metadata: multiLanguageMetadata,
        reviewedLanguages: Array.from(reviewedLanguages),
        correlationId,
        imageUrls,  // Include image URLs for Shopify product images
        shopify_product_id: refinedData?.shopify_product_id || draftProductId, // Include Shopify ID for updates or use draft ID
        status: status
      }, {
        showToast: true
      });

      if (uploadResponse.data) {
        const responseData = uploadResponse.data as { product_id?: string; shopify_product_id?: string };
        const productId = responseData.product_id || responseData.shopify_product_id;
        
        toast.success('Produkt erfolgreich in allen Sprachen veröffentlicht!');
        resetForm();
        
        // Navigate to product details page if we have a product ID
        if (productId) {
          navigate(`/products/${productId}`);
        } else {
          // Fallback to products list if no product ID returned
          navigate('/products');
        }
      } else {
        const errorMessage = uploadResponse.error || 'Unbekannter Fehler';
        toast.error(`Veröffentlichung fehlgeschlagen: ${errorMessage}`);
        setUploading(false);
        setUploadProgress(null);
      }
    } catch (error) {
      console.error('Publishing failed:', error);
      toast.error('Veröffentlichung fehlgeschlagen. Bitte versuchen Sie es erneut.');
      setUploading(false);
      setUploadProgress(null);
    }
  };
  const resetForm = () => {
    // If there's analyzed data, ask for confirmation before resetting
    if (multiLanguageMetadata && formState === 'analyzed') {
      const confirmReset = window.confirm(
        'Möchten Sie wirklich ein neues Produkt beginnen? Alle nicht veröffentlichten Änderungen gehen verloren.\n\n' +
        'Tipp: Speichern Sie das Produkt als Entwurf, bevor Sie fortfahren.'
      );
      if (!confirmReset) {
        return;
      }
    }
    
    setSelectedFiles([]);
    setMultiLanguageMetadata(null);
    setFormState('initial');
    setReviewedLanguages(new Set());
    setHasChanges({});
    setActiveLanguage('de');
    setCorrelationId(null);
    setImageUrls([]);  // Clear image URLs
    setUploading(false);
    setUploadProgress(null);
    setNameConflict(null);
    setValidatingName(false);
    setDraftProductId(null);  // Clear draft product ID
    if (onClearRefinedData) {
      onClearRefinedData();
    }
  };

  const getCurrentMetadata = () => {
    return multiLanguageMetadata?.[activeLanguage as keyof MultiLanguageMetadata];
  };

  const isLanguageReviewed = (languageCode: string) => {
    return reviewedLanguages.has(languageCode);
  };

  const allLanguagesReviewed = reviewedLanguages.size === LANGUAGES.length;

  return (
    <div className="max-w-6xl mx-auto">
      <div className="md:flex md:items-center md:justify-between mb-8">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-pottery-earth sm:text-3xl sm:truncate">
            Neues Inventar hinzufügen
          </h2>
          <p className="mt-1 text-sm text-clay-600">
            {formState === 'initial' 
              ? 'Laden Sie bis zu 5 hochwertige Bilder hoch. Die KI generiert automatisch mehrsprachige Inhalte.'
              : formState === 'analyzed'
              ? 'Überprüfen Sie die generierten Inhalte in allen Sprachen und verfeinern Sie sie bei Bedarf'
              : 'Verarbeitung läuft...'
            }
          </p>
        </div>
        {formState !== 'initial' && (
          <div className="mt-4 flex md:mt-0 md:ml-4">
            <button
              onClick={resetForm}
              className="inline-flex items-center px-4 py-2 border border-clay-300 rounded-md shadow-sm text-sm font-medium text-clay-700 bg-pottery-cream hover:bg-clay-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-clay-500"
            >
              Neues Produkt
            </button>
          </div>
        )}
      </div>

      {/* Progress indicator for real-time updates */}
      {uploading && uploadProgress && (
        <div className="mb-6 bg-white rounded-lg shadow p-4 sticky bottom-0 sm:static z-20 sm:z-auto sm:rounded-lg sm:p-4 border border-clay-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-900">{uploadProgress.message}</h3>
              <p className="text-sm text-gray-500">{uploadProgress.stage}</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">{uploadProgress.progress}%</div>
              {formState === 'processing' && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="inline-flex items-center px-3 py-1 border border-red-300 text-xs font-medium rounded text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <XMarkIcon className="h-4 w-4 mr-1" />
                  Abbrechen
                </button>
              )}
            </div>
          </div>
          <div className="mt-2">
            <div className="bg-gray-200 rounded-full h-2">
              <div 
                className="bg-clay-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress.progress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleAnalyze} className="space-y-8">
        {/* Image Upload Section */}
        <div className="bg-pottery-cream shadow px-4 py-5 sm:rounded-lg sm:p-6 border border-clay-200">
          <div className="md:grid md:grid-cols-3 md:gap-6">
            <div className="md:col-span-1">
              <h3 className="text-lg font-medium leading-6 text-pottery-earth">Produktbilder</h3>
              <p className="mt-1 text-sm text-clay-600">
                Laden Sie bis zu 5 hochwertige Bilder Ihrer Töpferei hoch. Die KI generiert automatisch mehrsprachige Beschreibungen.
              </p>
            </div>
            <div className="mt-5 md:mt-0 md:col-span-2">
              <div
                {...getRootProps({
                  role: 'button',
                  tabIndex: 0,
                  onKeyDown: handleRootKeyDown
                })}
                className={`mt-1 flex justify-center px-6 pt-8 pb-8 border-2 border-dashed rounded-md transition-colors cursor-pointer touch-manipulation ${
                  isDragActive
                    ? 'border-clay-400 bg-clay-100'
                    : 'border-clay-300 hover:border-clay-400 active:border-clay-500'
                }`}
                style={{ minHeight: '120px' }}
              >
                <input {...getInputProps({ className: 'hidden', 'aria-label': 'Bilder auswählen' })} />
                <div className="space-y-2 text-center">
                  <svg
                    className="mx-auto h-12 w-12 text-clay-400"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                  >
                    <path
                      d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-center sm:items-center text-sm text-clay-600">
                    <button
                      type="button"
                      onClick={handleSelectFilesClick}
                      className="hidden sm:inline-flex items-center px-3 py-2 border border-clay-300 rounded-md text-clay-700 bg-white hover:bg-clay-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-clay-500"
                    >
                      📷 Bilder auswählen
                    </button>
                    <button
                      type="button"
                      onClick={handleSelectFilesClick}
                      className="inline-flex sm:hidden items-center px-4 py-2 border border-clay-300 rounded-md text-clay-700 bg-white hover:bg-clay-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-clay-500"
                    >
                      📱 Fotos auswählen
                    </button>
                    <p className="text-xs sm:text-sm text-clay-500 sm:pl-2">
                      <span className="hidden sm:inline">oder Dateien hierher ziehen</span>
                      <span className="sm:hidden">Tippen Sie zum Auswählen</span>
                    </p>
                  </div>
                  <p className="text-xs text-clay-500">
                    PNG, JPG, WEBP, HEIC bis zu 10MB (max. 5 Bilder)
                  </p>
                  {selectedFiles.length > 0 && (
                    <p className="text-sm font-medium text-clay-700">
                      ✅ {selectedFiles.length} von 5 Bildern ausgewählt
                    </p>
                  )}
                  {isDragActive && (
                    <p className="text-sm font-medium text-clay-600">
                      📁 Bilder hier ablegen...
                    </p>
                  )}
                </div>
              </div>

              {/* Selected Files */}
              {selectedFiles.length > 0 && (
                <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Preview ${index + 1}`}
                        className="h-20 w-full object-cover rounded-lg border border-clay-200"
                      />
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        title="Bild entfernen"
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Optional Artist Hints Section - Only shown before analysis */}
        {formState === 'initial' && (
          <div className="bg-pottery-cream shadow px-4 py-5 sm:rounded-lg sm:p-6 border border-clay-200">
            <div className="md:grid md:grid-cols-3 md:gap-6">
              <div className="md:col-span-1">
                <h3 className="text-lg font-medium leading-6 text-pottery-earth">Zusätzliche Angaben</h3>
                <p className="mt-1 text-sm text-clay-600">
                  Optionale Informationen, die der KI bei der Analyse helfen. Alle Angaben sind freiwillig.
                </p>
              </div>
              <div className="mt-5 md:mt-0 md:col-span-2 space-y-6">
                {/* Name Idea */}
                <div>
                  <label htmlFor="nameIdea" className="block text-sm font-medium text-gray-700 mb-1">
                    Namensidee
                    <span className="ml-1 text-xs text-gray-400">(optional)</span>
                  </label>
                  <input
                    id="nameIdea"
                    type="text"
                    value={nameIdea}
                    onChange={(e) => setNameIdea(e.target.value)}
                    placeholder="z.B. Morgentau-Vase, Herbstblatt-Schale..."
                    className="mt-2 block w-full border border-warmGray-300 rounded-md shadow-sm focus:ring-clay-600 focus:border-clay-600"
                  />
                </div>

                {/* Inspiration */}
                <div>
                  <label htmlFor="inspiration" className="block text-sm font-medium text-gray-700 mb-1">
                    Inspiration
                    <span className="ml-1 text-xs text-gray-400">(optional)</span>
                  </label>
                  <AutoGrowTextarea
                    id="inspiration"
                    minRows={2}
                    maxRows={8}
                    value={inspiration}
                    onChange={(e) => setInspiration(e.target.value)}
                    placeholder="Was hat Sie zu diesem Stück inspiriert? z.B. Ein Waldspaziergang, japanische Keramik..."
                    className="mt-2 block w-full border border-warmGray-300 rounded-md shadow-sm focus:ring-clay-600 focus:border-clay-600"
                  />
                </div>

                {/* Dimensions */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Abmessungen
                    <span className="ml-1 text-xs text-gray-400">(optional, in cm)</span>
                  </label>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label htmlFor="heightCm" className="block text-xs text-gray-500 mb-1">Höhe</label>
                      <div className="relative">
                        <input
                          id="heightCm"
                          type="number"
                          step="0.1"
                          min="0"
                          value={heightCm}
                          onChange={(e) => setHeightCm(e.target.value)}
                          placeholder="0"
                          className="block w-full border border-warmGray-300 rounded-md shadow-sm focus:ring-clay-600 focus:border-clay-600 pr-10"
                        />
                        <span className="absolute inset-y-0 right-2 flex items-center text-xs text-gray-400">cm</span>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="widthCm" className="block text-xs text-gray-500 mb-1">Breite</label>
                      <div className="relative">
                        <input
                          id="widthCm"
                          type="number"
                          step="0.1"
                          min="0"
                          value={widthCm}
                          onChange={(e) => setWidthCm(e.target.value)}
                          placeholder="0"
                          className="block w-full border border-warmGray-300 rounded-md shadow-sm focus:ring-clay-600 focus:border-clay-600 pr-10"
                        />
                        <span className="absolute inset-y-0 right-2 flex items-center text-xs text-gray-400">cm</span>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="depthCm" className="block text-xs text-gray-500 mb-1">Tiefe</label>
                      <div className="relative">
                        <input
                          id="depthCm"
                          type="number"
                          step="0.1"
                          min="0"
                          value={depthCm}
                          onChange={(e) => setDepthCm(e.target.value)}
                          placeholder="0"
                          className="block w-full border border-warmGray-300 rounded-md shadow-sm focus:ring-clay-600 focus:border-clay-600 pr-10"
                        />
                        <span className="absolute inset-y-0 right-2 flex items-center text-xs text-gray-400">cm</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Weight and Price in a row */}
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="weightGrams" className="block text-sm font-medium text-gray-700 mb-1">
                      Gewicht
                      <span className="ml-1 text-xs text-gray-400">(optional)</span>
                    </label>
                    <div className="relative">
                      <input
                        id="weightGrams"
                        type="number"
                        step="1"
                        min="0"
                        value={weightGrams}
                        onChange={(e) => setWeightGrams(e.target.value)}
                        placeholder="0"
                        className="block w-full border border-warmGray-300 rounded-md shadow-sm focus:ring-clay-600 focus:border-clay-600 pr-10"
                      />
                      <span className="absolute inset-y-0 right-2 flex items-center text-xs text-gray-400">g</span>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="priceIdea" className="block text-sm font-medium text-gray-700 mb-1">
                      Preisidee
                      <span className="ml-1 text-xs text-gray-400">(optional)</span>
                    </label>
                    <div className="relative">
                      <input
                        id="priceIdea"
                        type="number"
                        step="0.01"
                        min="0"
                        value={priceIdea}
                        onChange={(e) => setPriceIdea(e.target.value)}
                        placeholder="0.00"
                        className="block w-full border border-warmGray-300 rounded-md shadow-sm focus:ring-clay-600 focus:border-clay-600 pr-10"
                      />
                      <span className="absolute inset-y-0 right-2 flex items-center text-xs text-gray-400">€</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Multi-Language Metadata Section */}
        {multiLanguageMetadata && (
          <div className="bg-white shadow sm:rounded-lg">
            {/* Language Tabs */}
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex overflow-x-auto px-2 sm:px-4 space-x-4 sm:space-x-8" aria-label="Tabs">
                {LANGUAGES.map((language) => (
                  <button
                    key={language.code}
                    type="button"
                    onClick={() => setActiveLanguage(language.code)}
                    className={`${
                      activeLanguage === language.code
                        ? 'border-clay-500 text-clay-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } flex-shrink-0 whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
                  >
                    <span>{language.flag}</span>
                    <span>{language.name}</span>
                    {isLanguageReviewed(language.code) && (
                      <CheckIcon className="h-4 w-4 text-green-500" />
                    )}
                    {hasChanges[language.code] && (
                      <span className="h-2 w-2 bg-orange-500 rounded-full" />
                    )}
                  </button>
                ))}
              </nav>
            </div>

            {/* Active Language Content */}
            <div className="px-4 py-5 sm:p-6">
              {getCurrentMetadata() && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label htmlFor={`name-${activeLanguage}`} className="block text-sm font-medium text-gray-700">
                        Name
                        {isFieldChanged(activeLanguage, 'name') && (
                          <span className="ml-2 text-xs text-amber-600">(geändert)</span>
                        )}
                      </label>
                      <input
                        id={`name-${activeLanguage}`}
                        type="text"
                        value={getCurrentMetadata()?.name || ''}
                        onChange={(e) => {
                          handleMetadataChange(activeLanguage, 'name', e.target.value);
                          // Clear name conflict when user changes the name
                          if (nameConflict) {
                            setNameConflict(null);
                          }
                        }}
                        placeholder="Produktname eingeben"
                        className={getFieldClassName(
                          activeLanguage,
                          'name',
                          `mt-1 block w-full rounded-md shadow-sm focus:ring-clay-600 focus:border-clay-600 sm:text-sm ${
                            nameConflict?.exists 
                              ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                              : 'border-warmGray-300'
                          }`
                        )}
                      />
                      {nameConflict?.exists && (
                        <div className="mt-2 flex items-center text-sm text-red-600">
                          <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                          <span>
                            Ein Produkt mit diesem Namen existiert bereits: &quot;{nameConflict.existingProduct?.title}&quot;
                          </span>
                        </div>
                      )}
                    </div>

                    <div>
                      <label htmlFor={`category-${activeLanguage}`} className="block text-sm font-medium text-gray-700">
                        Kategorie
                        {isFieldChanged(activeLanguage, 'category') && (
                          <span className="ml-2 text-xs text-amber-600">(geändert)</span>
                        )}
                      </label>
                      <CategoryCombobox
                        id={`category-${activeLanguage}`}
                        value={getCurrentMetadata()?.category || ''}
                        onChange={(value) => handleMetadataChange(activeLanguage, 'category', value)}
                        placeholder="Kategorie wählen oder eingeben"
                        className={getFieldClassName(
                          activeLanguage,
                          'category',
                          "mt-1"
                        )}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor={`description-${activeLanguage}`} className="block text-sm font-medium text-gray-700">
                      Beschreibung
                      {isFieldChanged(activeLanguage, 'description') && (
                        <span className="ml-2 text-xs text-amber-600">(geändert)</span>
                      )}
                    </label>
                    <AutoGrowTextarea
                      id={`description-${activeLanguage}`}
                      minRows={3}
                      maxRows={15}
                      value={getCurrentMetadata()?.description || ''}
                      onChange={(e) => handleMetadataChange(activeLanguage, 'description', e.target.value)}
                      placeholder="Produktbeschreibung eingeben"
                      className={getFieldClassName(
                        activeLanguage,
                        'description',
                        "mt-1 block w-full border border-warmGray-300 rounded-md shadow-sm focus:ring-clay-600 focus:border-clay-600"
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                    <div>
                      <label htmlFor={`price-${activeLanguage}`} className="block text-sm font-medium text-gray-700">
                        Preis
                        {isFieldChanged(activeLanguage, 'price') && (
                          <span className="ml-2 text-xs text-amber-600">(geändert)</span>
                        )}
                      </label>
                      <div className="mt-1 relative">
                        <input
                          id={`price-${activeLanguage}`}
                          type="number"
                          step="0.01"
                          value={getCurrentMetadata()?.price || 0}
                          onChange={(e) => handleMetadataChange(activeLanguage, 'price', parseFloat(e.target.value))}
                          placeholder="0.00"
                          className={getFieldClassName(
                            activeLanguage,
                            'price',
                            "block w-full border-warmGray-300 rounded-md shadow-sm focus:ring-clay-600 focus:border-clay-600 sm:text-sm pr-8"
                          )}
                        />
                        <span className="absolute inset-y-0 right-2 flex items-center text-xs text-gray-400">€</span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Materialien
                        {isFieldChanged(activeLanguage, 'materials') && (
                          <span className="ml-2 text-xs text-amber-600">(geändert)</span>
                        )}
                      </label>
                      <input
                        type="text"
                        value={(getCurrentMetadata()?.materials && Array.isArray(getCurrentMetadata()?.materials)) ? getCurrentMetadata()?.materials.join(', ') : ''}
                        onChange={(e) => handleArrayChange(activeLanguage, 'materials', e.target.value)}
                        placeholder="Keramik, Glasur"
                        className={getFieldClassName(
                          activeLanguage,
                          'materials',
                          "mt-1 block w-full border-warmGray-300 rounded-md shadow-sm focus:ring-clay-600 focus:border-clay-600 sm:text-sm"
                        )}
                      />
                    </div>
                  </div>

                  {/* Tags / Keywords with enhanced selector */}
                  <div className="col-span-full">
                    <KeywordSelector
                      label="Tags / Keywords"
                      value={Array.isArray(getCurrentMetadata()?.tags) ? getCurrentMetadata()!.tags : []}
                      onChange={(keywords) => handleMetadataChange(activeLanguage, 'tags', keywords)}
                      existingKeywords={existingKeywords}
                      isChanged={isFieldChanged(activeLanguage, 'tags')}
                      placeholder="Neues Keyword hinzufügen..."
                    />
                  </div>

                  {/* Action buttons for current language */}
                  <div className="flex flex-col gap-3 pt-4 border-t border-gray-200 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                      {hasChanges[activeLanguage] && (
                        <>
                          {/* Change summary badge */}
                          {getChangedFields(activeLanguage).length > 0 && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                              {getChangedFields(activeLanguage).length} {getChangedFields(activeLanguage).length === 1 ? 'Feld' : 'Felder'} geändert
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => handleRefine(activeLanguage)}
                            disabled={uploading}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
                            title={`Verfeinert: ${getChangedFields(activeLanguage).join(', ')}`}
                          >
                            {uploading ? 'Verfeinern...' : 'Änderungen übernehmen'}
                          </button>
                        </>
                      )}
                      {!isLanguageReviewed(activeLanguage) && (
                        <button
                          type="button"
                          onClick={() => markLanguageAsReviewed(activeLanguage)}
                          className="inline-flex items-center px-4 py-2 border border-green-300 text-sm font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                          <CheckIcon className="h-4 w-4 mr-1" />
                          Als überprüft markieren
                        </button>
                      )}
                    </div>
                    
                    <div className="text-sm text-gray-500">
                      {isLanguageReviewed(activeLanguage) ? (
                        <span className="text-green-600 flex items-center">
                          <CheckIcon className="h-4 w-4 mr-1" />
                          Überprüft
                        </span>
                      ) : (
                        'Noch nicht überprüft'
                      )}
                    </div>
                  </div>

                  {/* Dimensions and Weight Section - positioned below text confirmation area per #114 */}
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Abmessungen & Gewicht</h4>
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                      <div>
                        <label htmlFor={`height-${activeLanguage}`} className="block text-xs text-gray-500">Höhe</label>
                        <div className="mt-1 relative">
                          <input
                            id={`height-${activeLanguage}`}
                            type="number"
                            step="0.1"
                            min="0"
                            value={getCurrentMetadata()?.dimensions?.height || 0}
                            onChange={(e) => {
                              const newDimensions = {
                                ...getCurrentMetadata()?.dimensions,
                                height: parseFloat(e.target.value) || 0
                              };
                              handleMetadataChange(activeLanguage, 'dimensions', newDimensions);
                            }}
                            placeholder="0"
                            className="block w-full border-warmGray-300 rounded-md shadow-sm focus:ring-clay-600 focus:border-clay-600 sm:text-sm pr-8"
                          />
                          <span className="absolute inset-y-0 right-2 flex items-center text-xs text-gray-400">cm</span>
                        </div>
                      </div>
                      <div>
                        <label htmlFor={`width-${activeLanguage}`} className="block text-xs text-gray-500">Breite</label>
                        <div className="mt-1 relative">
                          <input
                            id={`width-${activeLanguage}`}
                            type="number"
                            step="0.1"
                            min="0"
                            value={getCurrentMetadata()?.dimensions?.width || 0}
                            onChange={(e) => {
                              const newDimensions = {
                                ...getCurrentMetadata()?.dimensions,
                                width: parseFloat(e.target.value) || 0
                              };
                              handleMetadataChange(activeLanguage, 'dimensions', newDimensions);
                            }}
                            placeholder="0"
                            className="block w-full border-warmGray-300 rounded-md shadow-sm focus:ring-clay-600 focus:border-clay-600 sm:text-sm pr-8"
                          />
                          <span className="absolute inset-y-0 right-2 flex items-center text-xs text-gray-400">cm</span>
                        </div>
                      </div>
                      <div>
                        <label htmlFor={`depth-${activeLanguage}`} className="block text-xs text-gray-500">Tiefe</label>
                        <div className="mt-1 relative">
                          <input
                            id={`depth-${activeLanguage}`}
                            type="number"
                            step="0.1"
                            min="0"
                            value={getCurrentMetadata()?.dimensions?.depth || 0}
                            onChange={(e) => {
                              const newDimensions = {
                                ...getCurrentMetadata()?.dimensions,
                                depth: parseFloat(e.target.value) || 0
                              };
                              handleMetadataChange(activeLanguage, 'dimensions', newDimensions);
                            }}
                            placeholder="0"
                            className="block w-full border-warmGray-300 rounded-md shadow-sm focus:ring-clay-600 focus:border-clay-600 sm:text-sm pr-8"
                          />
                          <span className="absolute inset-y-0 right-2 flex items-center text-xs text-gray-400">cm</span>
                        </div>
                      </div>
                      <div>
                        <label htmlFor={`weight-${activeLanguage}`} className="block text-xs text-gray-500">Gewicht</label>
                        <div className="mt-1 relative">
                          <input
                            id={`weight-${activeLanguage}`}
                            type="number"
                            step="1"
                            min="0"
                            value={getCurrentMetadata()?.dimensions?.weight || 0}
                            onChange={(e) => {
                              const newDimensions = {
                                ...getCurrentMetadata()?.dimensions,
                                weight: parseFloat(e.target.value) || 0
                              };
                              handleMetadataChange(activeLanguage, 'dimensions', newDimensions);
                            }}
                            placeholder="0"
                            className="block w-full border-warmGray-300 rounded-md shadow-sm focus:ring-clay-600 focus:border-clay-600 sm:text-sm pr-8"
                          />
                          <span className="absolute inset-y-0 right-2 flex items-center text-xs text-gray-400">g</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          {formState === 'initial' && (
            <>
              <button
                type="button"
                onClick={() => handleSaveDraft()}
                disabled={selectedFiles.length === 0 || uploading}
                className="inline-flex items-center px-4 py-2 border border-blue-300 text-sm font-medium rounded-md shadow-sm text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                Als Entwurf speichern
              </button>
              <button
                type="submit"
                disabled={selectedFiles.length === 0 || uploading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-clay-600 hover:bg-clay-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-clay-500 disabled:opacity-50"
              >
                {uploading ? 'Analysieren...' : 'Analysieren'}
              </button>
            </>
          )}

          {formState === 'analyzed' && (
            <>
              <button
                type="button"
                onClick={() => handlePublish('draft')}
                disabled={uploading}
                className={`inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-clay-500 disabled:opacity-50 mr-3`}
              >
                Als Entwurf speichern
              </button>
              <button
                type="button"
                onClick={() => handlePublish('active')}
                disabled={!allLanguagesReviewed || uploading || validatingName}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-clay-500 disabled:opacity-50 ${
                  allLanguagesReviewed && !nameConflict?.exists
                    ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' 
                    : nameConflict?.exists
                    ? 'bg-red-400'
                    : 'bg-gray-400'
                }`}
              >
                {validatingName ? 'Überprüfen...' : uploading ? 'Veröffentlichen...' : 'Veröffentlichen'}
                {allLanguagesReviewed && !nameConflict?.exists && !validatingName && !uploading && <CheckIcon className="ml-2 h-4 w-4" />}
              </button>
            </>
          )}
        </div>
      </form>

      {/* Review Status Summary */}
      {multiLanguageMetadata && (
        <div className="mt-6 bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Überprüfungsstatus</h4>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {LANGUAGES.map((language) => (
              <div key={language.code} className="flex items-center space-x-2">
                <span>{language.flag}</span>
                <span className="text-sm text-gray-600">{language.name}</span>
                {isLanguageReviewed(language.code) ? (
                  <CheckIcon className="h-4 w-4 text-green-500" />
                ) : (
                  <div className="h-4 w-4 border border-gray-300 rounded" />
                )}
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs text-gray-500">
            {reviewedLanguages.size} von {LANGUAGES.length} Sprachen überprüft
          </p>
        </div>
      )}

      {/* Progress Indicator */}
      <ProgressIndicator 
        correlationId={correlationId || undefined}
        onComplete={() => {
          // console.log('Progress completed');
        }}
      />
    </div>
  );
};

export default UploadFormEnhanced;